/**
 * Componente que escucha eventos 403 del backend y recarga los permisos.
 *
 * Flujo:
 * 1. Una petición al backend devuelve 403
 * 2. El interceptor global de fetch dispara PERMISSION_DENIED_EVENT
 * 3. Este handler recarga los permisos del usuario
 * 4. Si el usuario ya no tiene acceso a la ruta actual → redirige a /access-denied
 * 5. Muestra un toast: "Tus permisos han cambiado"
 *
 * Protecciones anti-loop:
 * - Debounce de 15 segundos entre recargas
 * - No se dispara si ya está recargando
 * - El interceptor de fetch excluye URLs de carga de permisos
 * - El reload usa userId cacheado si getUserByEmail falla
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "@/contexts/permissions-context";
import { PERMISSION_DENIED_EVENT } from "@/lib/api-client";
import { Snackbar, Alert } from "@mui/material";

const DEBOUNCE_MS = 15_000;

export function PermissionDeniedHandler() {
  const { reload, canAccess } = usePermissions();
  const navigate = useNavigate();
  const isHandlingRef = useRef(false);
  const lastHandledRef = useRef(0);
  const [toast, setToast] = useState(false);

  const handlePermissionDenied = useCallback(async () => {
    const now = Date.now();

    // Debounce: ignorar si ya estamos manejando o si se manejó hace menos de 15s
    if (isHandlingRef.current) return;
    if (now - lastHandledRef.current < DEBOUNCE_MS) return;

    isHandlingRef.current = true;
    lastHandledRef.current = now;

    console.warn("[PermissionDeniedHandler] 403 detectado. Recargando permisos...");

    try {
      await reload();
    } catch (err) {
      console.error("[PermissionDeniedHandler] Error recargando permisos:", err);
      isHandlingRef.current = false;
      return;
    }

    // Mostrar toast
    setToast(true);

    // Verificar acceso a la ruta actual después de que el state se actualice
    setTimeout(() => {
      const currentPath = window.location.pathname;
      // Usar canAccess del próximo render cycle — verificar si la ruta es accesible
      // Si no lo es, redirigir a access-denied
      if (!canAccess(currentPath)) {
        console.warn("[PermissionDeniedHandler] El usuario perdió acceso a:", currentPath);
        navigate("/access-denied", { replace: true });
      }
      isHandlingRef.current = false;
    }, 1000);
  }, [reload, canAccess, navigate]);

  useEffect(() => {
    const handler = () => handlePermissionDenied();
    window.addEventListener(PERMISSION_DENIED_EVENT, handler);
    return () => window.removeEventListener(PERMISSION_DENIED_EVENT, handler);
  }, [handlePermissionDenied]);

  return (
    <Snackbar
      open={toast}
      autoHideDuration={5000}
      onClose={() => setToast(false)}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
    >
      <Alert
        onClose={() => setToast(false)}
        severity="warning"
        variant="filled"
        sx={{ width: "100%" }}
      >
        Tus permisos han cambiado
      </Alert>
    </Snackbar>
  );
}
